import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import { Button, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import ChallanFormModal from "./AddChallans";
import ChallanMobileView from "./ChallanMobileView";
import MasterViewController from "../../redux/actions/masterApiActions";
import ExportButton from "../../components/ag grid/ExportButton";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";
import { getUserInfoFromToken } from "../../utils/authUtils";

import "../../styles/styles.css";
import "../../styles/layout.css";

const EXPORT_COLUMNS = ["name", "amount", "percentage", "applicable_salary_component"];

const ChallanMasterList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hasPermission } = usePermission();

  const [userType, setUserType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editChallan, setEditChallan] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const { getChallanMaster, deleteChallan } = MasterViewController();
  const { challans } = useSelector((state) => state.master);

  const canCreate =
    userType === "Admin" || hasPermission("challan_master", "create");
  const canEdit =
    userType === "Admin" || hasPermission("challan_master", "edit");
  const canDelete = hasPermission("challan_master", "delete");
  const canExport =
    userType === "Admin" || hasPermission("challan_master", "export");

  useEffect(() => {
    dispatch(getChallanMaster());
  }, [dispatch]);

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  const handleDelete = useCallback(
    (id) => dispatch(deleteChallan(id)),
    [dispatch, deleteChallan]
  );

  const handleEdit = useCallback((row) => {
    setEditChallan(row);
    setShowModal(true);
  }, []);

  const openAddModal = useCallback(() => {
    setEditChallan(null);
    setShowModal(true);
  }, []);

  const handleToggleSelect = useCallback((challan) => {
    setSelectedRows((prev) => {
      const exists = prev.some((row) => row.id === challan.id);
      if (exists) return prev.filter((row) => row.id !== challan.id);
      return [...prev, challan];
    });
  }, []);

  const handleMobileExport = useCallback(() => {
    if (selectedRows.length === 0) return;
    const filteredRows = selectedRows.map((row) => {
      const filtered = {};
      EXPORT_COLUMNS.forEach((key) => {
        filtered[key] = row[key];
      });
      return filtered;
    });
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "challans.xlsx");
  }, [selectedRows]);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR No.",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Name",
        field: "name",
        flex: 1,
        minWidth: 160,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Amount",
        field: "amount",
        width: 110,
        maxWidth: 120,
        minWidth: 110,
        suppressSizeToFit: true,
      },
      {
        headerName: "Percentage",
        field: "percentage",
        width: 110,
        maxWidth: 120,
        minWidth: 110,
        suppressSizeToFit: true,
      },
      {
        headerName: "Applicable Component",
        field: "applicable_salary_component",
        flex: 1,
        minWidth: 160,
        cellClass: "ag-cell-ellipsis",
      },
      ...(canEdit || canDelete
        ? [
            {
              headerName: "Actions",
              field: "actions",
              pinned: "right",
              width: 100,
              minWidth: 100,
              maxWidth: 100,
              suppressSizeToFit: true,
              sortable: false,
              cellRenderer: (params) => (
                <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(params.data)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDelete(params.data.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canEdit, canDelete, handleEdit, handleDelete]
  );

  return (
    <>
      {isMobile ? (
        <ChallanMobileView
          challans={challans || []}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={openAddModal}
          onExport={handleMobileExport}
          canCreate={canCreate}
          canEdit={canEdit}
          canDelete={canDelete}
          canExport={canExport}
        />
      ) : (
        <ListPageLayout
          title="Challan Master"
          subtitle="Configure statutory deductions and contribution rules"
          headerAction={
            canCreate ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutline />}
                onClick={openAddModal}
                sx={toolbarButtonSx}
              >
                Add Challan
              </Button>
            ) : null
          }
          toolbarRight={
            <>
              {canExport && (
                <ExportButton
                  gridRef={gridRef}
                  fileName="challans.xlsx"
                  exportColumns={EXPORT_COLUMNS}
                  disabled={selectedRows.length === 0}
                />
              )}
              <PageSizeSelector
                pageSize={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              />
            </>
          }
        >
          <div className="list-page-grid">
            <ReusableGrid
              rowData={challans}
              columnDefs={columnDefs}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              getRowId={(params) => String(params.data.id)}
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No challans configured"
              emptyHint={
                canCreate
                  ? 'Click "Add Challan" to create your first challan rule.'
                  : "No challan records are available."
              }
            />
          </div>
        </ListPageLayout>
      )}

      {showModal && (
        <ChallanFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editChallan}
        />
      )}
    </>
  );
};

export default ChallanMasterList;
